import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { AnalyticsDataModel } from '../models/analytics.datamodel';
import { round } from '@turf/turf';
import * as mixpanel from 'mixpanel-browser';
import { PRINTABLE_ACCESS_KEYS, PRISTINE_PERMISSIONS } from '../constants/analytics.constants';
import { Router } from '@angular/router';

@Injectable()
export class AnalyticsMixpanelService {
	public productCampaignsActiveCount: any = 0;

	constructor(private analyticsDataModel: AnalyticsDataModel, private router: Router) {}

	/**
	 * Initialize mixpanel.
	 */
	init(): void {
		mixpanel.init(this.analyticsDataModel.mixpanel.mixpanel_token);
	}

	/**
	 * Push new action to mixpanel.
	 *
	 * @param {string} id Name of the action to track.
	 * @param {*} [action={}] Actions object with custom properties.
	 * @memberof MixpanelService
	 */
	track(id: string, action: any = {}): void {
		mixpanel.track(id, action);
	}

	/**
	 * setup mixpannel
	 *
	 */
	setup() {
		//mixpanel.loggingEnabled = false;
		this.setSuperProperties({ Platform: 'web' });
	}

	/**
	 * Create new Alias for user
	 * Call this method only once in user lifecycle
	 *
	 * @param {string} alias
	 */
	createAlias(alias: string) {
		mixpanel.alias(alias, mixpanel.get_distinct_id());
	}

	/**
	 * Identify User
	 *
	 * @param {string} alias
	 */
	identify(alias: string) {
		mixpanel.identify(this.analyticsDataModel.mixpanel.u_id);
	}

	/**
	 * setPeople
	 * Store user profiles in Mixpanel's People Analytics product
	 * @param {Object} properties
	 */
	setPeople(properties: any = {}): void {
		mixpanel.people.set({
			$first_name: this.analyticsDataModel.mixpanel.firstName,
			$last_name: this.analyticsDataModel.mixpanel.lastName,
			$email: this.analyticsDataModel.mixpanel.email,
			user_type: this.analyticsDataModel.mixpanel.user_role,
			is_super: this.analyticsDataModel.mixpanel.user_is_super,
			partner_name: this.analyticsDataModel.mixpanel.user_partner,
			cname: this.analyticsDataModel.mixpanel.user_cname,
			advertiser_name: this.analyticsDataModel.mixpanel.user_advertiser_name,
			advertiser_id: this.analyticsDataModel.mixpanel.user_advertiser_id,
			timezone_offset: (new Date().getTimezoneOffset() / 60) * -1,
			last_page_visit: this.analyticsDataModel.mixpanel.page_access_time
		});
	}

	/**
	 * setPeopleOnce
	 * Store user profiles in Mixpanel's People Analytics product
	 * @param {Object} properties
	 */
	setPeopleOnce(page_access_time: any): void {
		mixpanel.people.set_once({
			$created: page_access_time,
			page_views: 0
		});
	}

	/**
	 * setPageView Increament
	 * Increament page view counter in Mixpanel's People Analytics product
	 * @param {Object} properties
	 */
	setPageViewIncreament(): void {
		mixpanel.people.increment('page_views');
	}

	/**
	 * setSuperProperties
	 *
	 * @param {object} properties
	 */
	setSuperProperties(properties) {
		mixpanel.register(properties);
	}

	/**
	 * sendEvent
	 *
	 * @param {string} event
	 * @param {object} properties
	 */
	sendEvent(event: string, properties?) {
		if (properties) {
			mixpanel.track(event, properties);
		} else {
			this.trackEvent(event);
		}
	}

	/**
	 * trackEvent
	 * @param {string} event
	 */
	trackEvent(event: string) {
		mixpanel.track(event);
	}

	/**
	 * Reset Mixpanel
	 */
	logout() {
		mixpanel.reset();
	}

	/**
	 * setUserProfile
	 * user identify and set the user profile
	 * @param {string} alias
	 * @param {Object} properties
	 */
	setUserProfile(alias: string, properties: any = {}) {
		this.init();
		this.identify(alias);
		this.setPeople(properties.page_access_time);
		this.setPeopleOnce(properties.page_access_time);
		this.setPageViewIncreament();
	}

	mixpanelData(event) {
		var formatted_start_date = new Date(this.analyticsDataModel.startDate._d).getTime();
		var formatted_end_date = new Date(this.analyticsDataModel.endDate._d).getTime();
		var date_diff = round((formatted_end_date - formatted_start_date) / (1000 * 3600 * 24));

		mixpanel.track(event, {
			reporting_advertisers_count: this.analyticsDataModel.selectedAdvertisers.length,
			reporting_campaigns_count: this.analyticsDataModel.selectedCampaigns.length,
			reporting_date_range_length: date_diff,
			analytics_config: this.analyticsDataModel.partner.analyticsConfig
		});
	}

	mixpanelProductClickData(event, productCampaignsActiveCount) {
		var formatted_start_date = new Date(this.analyticsDataModel.startDate._d).getTime();
		var formatted_end_date = new Date(this.analyticsDataModel.endDate._d).getTime();
		var date_diff = round((formatted_end_date - formatted_start_date) / (1000 * 3600 * 24));
		mixpanel.track(event, {
			reporting_advertisers_count: this.analyticsDataModel.selectedAdvertisers.length,
			reporting_campaigns_count: this.analyticsDataModel.selectedCampaigns.length,
			reporting_product_specific_active_campaigns: productCampaignsActiveCount,
			reporting_date_range_length: date_diff,
			analytics_config: this.analyticsDataModel.partner.analyticsConfig
		});
	}

	mixpanelOverviewProductClickData(event) {
		var formatted_start_date = new Date(this.analyticsDataModel.startDate._d).getTime();
		var formatted_end_date = new Date(this.analyticsDataModel.endDate._d).getTime();
		var date_diff = round((formatted_end_date - formatted_start_date) / (1000 * 3600 * 24));
		mixpanel.track(event, {
			reporting_advertisers_count: this.analyticsDataModel.selectedAdvertisers.length,
			reporting_campaigns_count: this.analyticsDataModel.selectedCampaigns.length,
			reporting_date_range_length: date_diff,
			analytics_config: this.analyticsDataModel.partner.analyticsConfig
		});
	}

	mixpanelBulkDownloadData(event, productCampaignsActiveCount, overviewprintablereport?) {
		var formatted_start_date = new Date(this.analyticsDataModel.startDate._d).getTime();
		var formatted_end_date = new Date(this.analyticsDataModel.endDate._d).getTime();
		var date_diff = round((formatted_end_date - formatted_start_date) / (1000 * 3600 * 24));
		if (this.analyticsDataModel.getCurrentPath() !== 'overview' && overviewprintablereport !== 'overview') {
			mixpanel.track(event, {
				reporting_advertisers_count: this.analyticsDataModel.selectedAdvertisers.length,
				reporting_product_specific_active_campaigns: productCampaignsActiveCount,
				reporting_date_range_length: date_diff,
				analytics_config: this.analyticsDataModel.partner.analyticsConfig
			});
		} else if (this.analyticsDataModel.getCurrentPath() === 'overview' || overviewprintablereport == 'overview') {
			mixpanel.track(event, {
				reporting_advertisers_count: this.analyticsDataModel.selectedAdvertisers.length,
				reporting_campaigns_count: productCampaignsActiveCount,
				reporting_date_range_length: date_diff,
				analytics_config: this.analyticsDataModel.partner.analyticsConfig
			});
		}
	}

	getCurrentPath(): string {
		const pathWithoutQueryParams = this.router.url.split('?')[0];
		const path: string[] = pathWithoutQueryParams.split('/');
		const currentURL = path.length > 1 ? path[2] : path[1];
		return currentURL;
	}

	productSelectedActiveCampaign(overviewprintablereport?): void {
		this.productCampaignsActiveCount = 0;
		let product;
		let supplementalProduct;
		this.analyticsDataModel.config.products.forEach((item) => {
			if (item.productURL === this.getCurrentPath()) {
				product = item;
			}
		});
		if (product?.printableReport?.supplementalCampaigns) {
			supplementalProduct = PRISTINE_PERMISSIONS[product.printableReport.supplementalCampaigns.permissions.flags[0]];
		}
		const mainProduct = PRINTABLE_ACCESS_KEYS[this.getCurrentPath().toUpperCase()];
		if (this.getCurrentPath().toUpperCase() !== 'OVERVIEW' && overviewprintablereport !== 'overview') {
			const count = this.analyticsDataModel.selectedCampaigns.reduce((sum, campaign) => {
				if (campaign.permission.includes(mainProduct) || campaign.permission.includes(supplementalProduct)) {
					this.productCampaignsActiveCount = campaign.active
						? this.productCampaignsActiveCount + 1
						: this.productCampaignsActiveCount;
					return (sum += 1);
				}
				return sum;
			}, 0);
		} else if (this.getCurrentPath().toUpperCase() === 'OVERVIEW' || overviewprintablereport == 'overview') {
			const count = this.analyticsDataModel.selectedCampaigns.reduce((sum, campaign) => {
				this.productCampaignsActiveCount = campaign.active
					? this.productCampaignsActiveCount + 1
					: this.productCampaignsActiveCount;
				return (sum += 1);
			}, 0);
		}
		return this.productCampaignsActiveCount;
	}

	mixpanelWidgetDownloadData(event) {
		mixpanel.track(event, {
			analytics_config: this.analyticsDataModel.partner.analyticsConfig
		});
	}
	userLoginTrack(event) {
		mixpanel.init(event['mixpanel']['mixpanel_token']);
		mixpanel.track('Analytics / User Login Status', {
			user_login_status: event['mixpanel']
		});
	}
}
